import { createBreakpoints } from '@mui/system';

import { extractKeys } from '@craft/lib/primitives';

import { isServer } from '../is-server';
import { quantitativeSizeToString } from '../sizes';

import { ThemeBreakpoints } from './breakpoints';
import {
  type FlatMediaQueryRange,
  getViewportModel,
  isSizeWithinViewport,
  type MediaQueryDimension,
  type MediaQueryObj,
  type MediaQueryRange,
  type RawMediaQueryObj,
  Viewport,
  type ViewportModel,
  Viewports,
} from './types';

interface ViewportMatchesScreenSizeWindowOptions {
  readonly size?: never;
  readonly window?: Window;
}

interface ViewportMatchesScreenSizeNoWindowOptions {
  readonly size: number;
  readonly window?: never;
}

type ViewportMatchesScreenSizeOptions =
  | ViewportMatchesScreenSizeNoWindowOptions
  | ViewportMatchesScreenSizeWindowOptions;

const viewportMatchesScreenSize = (
  viewport: ViewportModel,
  options: ViewportMatchesScreenSizeOptions,
): boolean => {
  if (options.window !== undefined) {
    options.size satisfies undefined;
    const query = stringifyMediaQueryObj({ width: viewport.id });
    return options.window.matchMedia(query).matches;
  } else if (options.size !== undefined) {
    return isSizeWithinViewport(options.size, viewport.id);
  } else if (!isServer()) {
    const query = stringifyMediaQueryObj({ width: viewport.id });
    return window.matchMedia(query).matches;
  }
  throw new Error('The "size" option must be provided when not in a browser environment!');
};

export class ViewportConfigurationError extends Error {
  constructor(message: string) {
    super(`Viewport Configuration Error: ${message}`);
    this.name = 'ViewportConfigurationError';
  }
}

/**
 * Returns the viewport, {@link Viewport}, associated with the provided either the provided
 * screen size (in pixels) or the current {@link Window} object (in cases where it is available
 * and the runtime is a browser environment).
 *
 * If the provided options include a {@link Window} object, then the method will attempt to
 * determine the {@link Viewport} based on CSS media queries and the breakpoint specifications
 * of each {@link Viewport}.  If the 'size' is provided, then the method will determine the viewport
 * based on a numeric comparison of that value and the breakpoint boundaries of each
 * {@link Viewport}.
 *
 * @param {ViewportMatchesScreenSizeOptions} options
 *   Options that include either the specification of the current 'size' or the global
 *   {@link Window} object in browser runtime environments.  If not provided, the method will
 *   use the global {@link Window} object if it is available, and will throw an error if it is not.
 *
 * @throws {ViewportConfigurationError}
 *   If the viewport configuration is invalid, such that the breakpoint boundaries of the viewports
 *   are not mutually exclusive.
 * @throws {Error}
 *   If the 'size' option is not provided in non-browser runtime environments.
 *
 * @returns {Viewport}
 *   The viewport, {@link Viewport}, whose breakpoint boundaries match the provided screen size
 *   or {@link Window}.
 */
export const getViewport = (options: ViewportMatchesScreenSizeOptions): Viewport => {
  const matchingViewports = [...Viewports].filter(viewport =>
    viewportMatchesScreenSize(viewport, options),
  );
  if (matchingViewports.length === 0) {
    const message =
      options.size === undefined
        ? 'No matching viewport found for current window!'
        : `No matching viewport found for screen size '${options.size}'!`;
    throw new ViewportConfigurationError(
      [message, 'This indicates that the viewports are not properly configured.'].join(' '),
    );
  } else if (matchingViewports.length !== 1) {
    const message =
      options.size === undefined
        ? 'Multiple matching viewports were found for current window!'
        : `Multiple matching viewports were found for screen size '${options.size}'!`;
    throw new ViewportConfigurationError(
      [message, 'This indicates that the viewports are not properly configured.'].join(' '),
    );
  }
  return matchingViewports[0].id;
};

type MediaQueryCssProperty = 'maxHeight' | 'maxWidth' | 'minHeight' | 'minWidth';

const MediaQueryCSSProperties = {
  maxHeight: 'max-height',
  maxWidth: 'max-width',
  minHeight: 'min-height',
  minWidth: 'min-width',
} as const satisfies Record<MediaQueryCssProperty, `${'max' | 'min'}-${'height' | 'width'}`>;

/**
 * Infers the type of the range, {@link Range}, based on the flexible range defined by the generic
 * type parameter {@link R}.
 */
type FlattenedRange<R extends MediaQueryRange<unknown, unknown>> = R extends {
  max: infer T;
  min: infer L;
}
  ? [T, L]
  : R extends { max: infer T }
    ? [null, T]
    : R extends { min: infer L }
      ? [L, null]
      : R extends FlatMediaQueryRange<unknown, unknown>
        ? R
        : never;

/**
 * Flattens the provided range, {@link MediaQueryRange}, into an array-form range, {@link Range}.
 */
const flattenRange = <R extends MediaQueryRange<unknown, unknown>>(range: R): FlattenedRange<R> => {
  if (Array.isArray(range)) {
    return range as FlattenedRange<R>;
  }
  if ('max' in range && 'min' in range) {
    return [range.min, range.max] as FlattenedRange<R>;
  } else if ('min' in range) {
    return [range.min, null] as FlattenedRange<R>;
  }
  return [null, range.max] as FlattenedRange<R>;
};

const stringifyMediaQueryValue = (value: number, dimension: MediaQueryCssProperty): string =>
  `(${MediaQueryCSSProperties[dimension]}:${quantitativeSizeToString(value, 'px')})`;

type MediaQueryPrefix = `@${string}`;

/**
 * Options that define how a given media query object, {@link MediaQueryObj}, should be converted
 * into a valid CSS media query string.
 */
interface StringifyMediaQueryOptions {
  /**
   * The prefix that should be applied to the resulting CSS media query string.  Right now, this
   * only supports the "@media" prefix.
   */
  readonly prefix?: MediaQueryPrefix | null;
}

const PrefixRegex = /^@([A-Za-z0-9]+)\s+.*$/;

/**
 * Attaches a prefix, {@link MediaQueryPrefix}, to the provided CSS media query string, ensuring
 * that only one prefix is present at a time.  If the provided prefix, {@link MediaQueryPrefix},
 * is null, then no prefix will be applied - and any existing prefix will be removed.
 *
 * @param {string} value The value that should be prefixed.
 * @param {Pick<StringifyMediaQueryOptions, 'prefix'>} options
 *   The options that dictate how the media query string should be formatted, which include the
 *   prefix that should or should not be applied, {@link MediaQueryPrefix}.
 *
 * @returns {string}
 *   The CSS media query string with the prefix, {@link MediaQueryPrefix}, applied or removed (
 *   depending on the options provided to the method,
 *   {@link Pick<StringifyMediaQueryOptions, 'prefix'>}.
 */
export const withMediaQueryPrefix = (
  value: string,
  options?: Pick<StringifyMediaQueryOptions, 'prefix'>,
) => {
  let naiveValue = value;
  const matches = PrefixRegex.exec(value);
  if (matches && matches.length >= 2) {
    const existingPrefix = `@${matches[1]}`;
    naiveValue = value.slice(existingPrefix.length).trim();
  }
  return options?.prefix ? `${options.prefix} ${naiveValue}` : naiveValue;
};

/**
 * Combines the provided CSS media query string(s) into a single CSS media query string.
 *
 * @param {string[]} queries The CSS media query strings that should be combined.
 *
 * @returns {string} The combined CSS media query string.
 */
const combineMediaQueries = (queries: string[]): string => {
  if (queries.length === 0) {
    return '';
  }
  return queries.map(query => withMediaQueryPrefix(query, { prefix: null })).join(' and ');
};

type MediaQueryTransformer<K extends MediaQueryDimension> = (query: RawMediaQueryObj[K]) => string;

const isViewport = (value: unknown): value is Viewport =>
  Object.values(Viewport).includes(value as Viewport);

const breakpoints = createBreakpoints({
  keys: extractKeys(ThemeBreakpoints),
  values: ThemeBreakpoints,
});

const MediaQueryTransformers: { [key in MediaQueryDimension]: MediaQueryTransformer<key> } = {
  height: query => {
    const range = flattenRange(query);
    if (range[0] !== null && range[1] !== null) {
      return combineMediaQueries([
        stringifyMediaQueryValue(range[0], 'minHeight'),
        stringifyMediaQueryValue(range[1] - 0.05, 'maxHeight'),
      ]);
    } else if (range[0] !== null) {
      return stringifyMediaQueryValue(range[0], 'minHeight');
    }
    return stringifyMediaQueryValue(range[1] - 0.05, 'maxHeight');
  },
  width: query => {
    if (isViewport(query)) {
      const viewport = getViewportModel(query);
      if (viewport.boundaries[0] === 'zero') {
        return breakpoints.down(viewport.boundaries[1]);
      } else if (viewport.boundaries[1] === 'infinity') {
        return breakpoints.up(viewport.boundaries[0]);
      }
      return breakpoints.only(viewport.boundaries[0]);
    }

    const range = flattenRange(query);

    if (isViewport(range[0])) {
      if (isViewport(range[1])) {
        return breakpoints.between(
          getViewportModel(range[0]).boundaries[0],
          getViewportModel(range[1]).boundaries[0],
        );
      }
      if (range[1] !== null) {
        return breakpoints.between(getViewportModel(range[0]).boundaries[0], range[1]);
      }
      return breakpoints.up(getViewportModel(range[0]).boundaries[0]);
    }

    if (isViewport(range[1])) {
      if (range[0] !== null) {
        return breakpoints.between(range[0], getViewportModel(range[1]).boundaries[0]);
      }
      return breakpoints.down(getViewportModel(range[1]).boundaries[0]);
    }

    if (range[0] !== null) {
      if (range[1] !== null) {
        return breakpoints.between(range[0], range[1]);
      }
      return breakpoints.up(range[0]);
    }

    return breakpoints.down(range[1]);
  },
};

/**
 * Converts the provided media query object, {@link MediaQueryObj}, into a valid string, CSS
 * media query.
 *
 * @param {MediaQueryObj} mediaQuery
 *   The media query object {@link MediaQueryObj}, that should be converted into a valid CSS media
 *   query string.
 * @param {StringifyMediaQueryOptions} options
 *   Options that dictate how the media query string should be formatted.
 *
 * @returns {string} A CSS media query string that represents the provided media query object.
 */
export const stringifyMediaQueryObj = (
  mediaQuery: MediaQueryObj,
  options?: StringifyMediaQueryOptions,
): string => {
  /* This type coercion is safe and is necessary because the stricter type subset of the media
     query keys is lost via the Object.keys() method, which returns an array of strings with no
     type awareness of the string literal type that the keys represent. */
  const dimensions = Object.keys(mediaQuery) as MediaQueryDimension[];
  if (dimensions.length === 0) {
    throw new Error(
      'Invalid Method Implementation: At least 1 dimension must be defined for the media query!',
    );
  }

  const queries = dimensions.map(dimension => {
    /* This type coercion is safe because the transformer function, which is obtained via a map
       lookup, is treated as a union of functions, rather than a function whose first argument is
       a union of types (due to the mapped type it is generated from). */
    const transformer = MediaQueryTransformers[dimension] as MediaQueryTransformer<
      keyof typeof mediaQuery
    >;
    if (mediaQuery[dimension] === undefined) {
      throw new Error(
        `Unexpectedly encountered undefined value for media query dimension '${dimension}'!`,
      );
    }
    return transformer(mediaQuery[dimension]);
  });
  return withMediaQueryPrefix(combineMediaQueries(queries), options);
};
